import React from 'react';
import Icon from '../../../components/AppIcon';

const PointsOverviewCard = ({ userPoints, tierProgress, hasProAccess }) => {
  const getTierColor = (tier) => {
    const colors = {
      Bronze: 'var(--color-warning)',
      Silver: 'var(--color-muted-foreground)',
      Gold: '#FFD700',
      Platinum: 'var(--color-primary)'
    };
    return colors?.[tier] || 'var(--color-muted-foreground)';
  };

  const getTierIcon = (tier) => {
    const icons = {
      Bronze: 'Award',
      Silver: 'Award',
      Gold: 'Crown',
      Platinum: 'Star'
    };
    return icons?.[tier] || 'Award';
  };

  return (
    <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 rounded-lg p-24 md:p-32 shadow-elevation-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-24">
        {/* Total Points */}
        <div className="md:col-span-1">
          <div className="flex items-center gap-12 mb-12">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/20 rounded-lg">
              <Icon name="Coins" size={24} color="var(--color-primary)" strokeWidth={2.5} />
            </div>
            <h3 className="text-sm font-caption font-medium text-muted-foreground uppercase tracking-wide">
              Available Points
            </h3>
          </div>
          <p className="text-4xl md:text-5xl font-heading font-bold text-secondary mb-8">
            {userPoints?.totalPoints?.toLocaleString() || 0}
          </p>
          <p className="text-sm text-muted-foreground">
            Lifetime earned: {userPoints?.lifetimeEarned?.toLocaleString() || 0}
          </p>
        </div>

        {/* Tier Status */}
        <div className="md:col-span-1">
          <div className="flex items-center gap-12 mb-12">
            <div 
              className="flex items-center justify-center w-12 h-12 rounded-lg"
              style={{ backgroundColor: `${getTierColor(userPoints?.tier)}15` }}
            >
              <Icon 
                name={getTierIcon(userPoints?.tier)} 
                size={24} 
                color={getTierColor(userPoints?.tier)} 
                strokeWidth={2.5} 
              />
            </div>
            <h3 className="text-sm font-caption font-medium text-muted-foreground uppercase tracking-wide">
              Current Tier
            </h3>
          </div>
          <p 
            className="text-3xl md:text-4xl font-heading font-bold mb-8"
            style={{ color: getTierColor(userPoints?.tier) }}
          >
            {userPoints?.tier || 'Bronze'}
          </p>
          {tierProgress?.nextTier && (
            <p className="text-sm text-muted-foreground">
              {tierProgress?.pointsToNext?.toLocaleString()} points to {tierProgress?.nextTier}
            </p>
          )}
        </div>

        {/* Streak & Pro Status */}
        <div className="md:col-span-1">
          <div className="space-y-16">
            {/* Streak */}
            <div>
              <div className="flex items-center gap-12 mb-8">
                <Icon name="Flame" size={20} color="var(--color-warning)" />
                <span className="text-sm font-caption font-medium text-muted-foreground">
                  Activity Streak
                </span>
              </div>
              <p className="text-2xl font-heading font-semibold text-secondary">
                {userPoints?.streakDays || 0} days
              </p>
            </div>

            {/* Pro Status */}
            <div>
              <div className="flex items-center gap-12 mb-8">
                <Icon name="Crown" size={20} color="var(--color-primary)" />
                <span className="text-sm font-caption font-medium text-muted-foreground">
                  Membership
                </span>
              </div>
              <span className={`inline-flex items-center gap-6 px-12 py-6 rounded-lg text-sm font-medium ${
                hasProAccess 
                  ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
              }`}>
                {hasProAccess ? 'Pro Member' : 'Free Member'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Progress Bar */}
      {tierProgress?.nextTier && (
        <div className="mt-24 pt-24 border-t border-border/50">
          <div className="flex items-center justify-between mb-8">
            <span className="text-sm font-medium text-muted-foreground">
              Progress to {tierProgress?.nextTier}
            </span>
            <span className="text-sm font-semibold text-primary">
              {tierProgress?.progress?.toFixed(1)}%
            </span>
          </div>
          <div className="w-full h-8 bg-muted/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-accent transition-smooth"
              style={{ width: `${Math.min(tierProgress?.progress, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsOverviewCard;